// Phase 7 D3 — Track C T-C5 + Track F T-F2. GmailConnectCard.
//
// Renders one of five states based on the result of useGmailStatus():
//   - loading       → Skeleton
//   - unconfigured  → informational card (Phase 7.5 T-B4 — operator hasn't
//                     provisioned GOOGLE_OAUTH_CLIENT_ID; no destructive tone,
//                     no retry)
//   - error         → inline error (genuine 5xx / network failures)
//   - disconnected  → "Connect Gmail" button (window.location.href to backend)
//   - connected     → first connected mailbox's email-hash prefix +
//                     destructive "Disconnect" button (with confirmation)
//
// Q-5: connect callback failure paths surface as toasts via the parent's
// useEffect on `?connect=denied|error` (see Integrations.tsx). This card
// is responsible only for the connected/disconnected primary affordance.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { endpoints } from '@dailydeck/shared/api';
import type { EmailConnection } from '@dailydeck/shared/types';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../app/apiClient.js';
import {
  useGmailStatus,
  useGmailDisconnect,
} from '../../../hooks/useGmailStatus.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { SkeletonRow } from '../../../components/ui/Skeleton.js';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../../../components/ui/Dialog.js';
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastClose,
} from '../../../components/ui/Toast.js';

function activeConnection(rows: readonly EmailConnection[]): EmailConnection | null {
  return rows.find((r) => r.status === 'connected') ?? null;
}

export function GmailConnectCard(): JSX.Element {
  const status = useGmailStatus();
  const disconnect = useGmailDisconnect();
  const [open, setOpen] = React.useState(false);
  const [toast, setToast] = React.useState<{ title: string; body?: string } | null>(null);

  const scanMutation = useMutation({
    mutationFn: (accountEmailHash: string) =>
      apiClient.payments.emailScan({
        accountEmailHash,
        eventId: `scan-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      }),
    onSuccess: (result) => {
      setToast({
        title: copy.settings.integrations.gmailScanSuccessToastTitle,
        body: copy.settings.integrations.gmailScanSuccessToastBody(result.detectedItems),
      });
    },
    onError: () => {
      setToast({
        title: copy.settings.integrations.gmailScanErrorToastTitle,
        body: copy.settings.integrations.gmailScanErrorToastBody,
      });
    },
  });

  function handleConnect(): void {
    // window.location.href so the SPA unmounts cleanly; the backend issues
    // the OAuth state cookie and 302s to Google. The callback redirects
    // back to /settings/integrations?connect=success|denied|error which
    // Integrations.tsx handles.
    window.location.href = endpoints.paymentsEmailConnect;
  }

  function handleDisconnect(connection: EmailConnection): void {
    disconnect.mutate(
      { accountEmailHash: connection.accountEmailHash },
      {
        onSuccess: () => {
          setOpen(false);
          setToast({ title: copy.settings.integrations.gmailDisconnectSuccessToast });
        },
      },
    );
  }

  if (status.kind === 'unconfigured') {
    return (
      <Card data-testid="gmail-connect-card">
        <CardHeader>
          <CardTitle>{copy.settings.integrations.gmailUnconfiguredTitle}</CardTitle>
          <CardDescription>
            {copy.settings.integrations.gmailUnconfiguredBody}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-body text-text-secondary" data-testid="gmail-unconfigured-body">
            {copy.settings.integrations.gmailUnconfiguredBody}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="gmail-connect-card">
      <CardHeader>
        <CardTitle>{copy.settings.integrations.gmailCardTitle}</CardTitle>
        <CardDescription>
          {copy.settings.integrations.gmailDisconnectedDescription}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status.kind === 'loading' && <SkeletonRow />}
        {status.kind === 'error' && (
          <div className="text-body text-state-danger">
            <p className="text-body-strong">
              {copy.settings.integrations.gmailLoadErrorTitle}
            </p>
            <p>{copy.settings.integrations.gmailLoadErrorBody}</p>
          </div>
        )}
        {status.kind === 'disconnected' && (
          <Button
            variant="primary"
            type="button"
            onClick={handleConnect}
            data-testid="gmail-connect-button"
          >
            {copy.settings.integrations.gmailConnectButton}
          </Button>
        )}
        {status.kind === 'connected' && (() => {
          const active = activeConnection(status.rows);
          if (active === null) {
            // Defensive — kind='connected' implies at least one row with
            // status='connected', but TS can't narrow that for us.
            return null;
          }
          // Show only the first 12 chars of the hash — privacy-preserving.
          const hashPrefix = active.accountEmailHash.slice(0, 12);
          return (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div data-testid="gmail-connected-info" className="text-body text-text-primary">
                <span className="text-text-secondary">
                  {copy.settings.integrations.gmailConnectedAs}
                </span>{' '}
                <span className="font-mono text-caption">{hashPrefix}…</span>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled={scanMutation.isPending}
                  aria-busy={scanMutation.isPending}
                  data-testid="gmail-scan-button"
                  onClick={() => scanMutation.mutate(active.accountEmailHash)}
                >
                  {scanMutation.isPending
                    ? copy.settings.integrations.gmailScanningLabel
                    : copy.settings.integrations.gmailScanButton}
                </Button>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" size="sm" type="button">
                    {copy.settings.integrations.gmailDisconnectButton}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>
                      {copy.settings.integrations.gmailDisconnectDialogTitle}
                    </DialogTitle>
                    <DialogDescription>
                      {copy.settings.integrations.gmailDisconnectDialogBody}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="secondary" type="button">
                        {copy.settings.integrations.gmailDisconnectDialogCancel}
                      </Button>
                    </DialogClose>
                    <Button
                      variant="destructive"
                      type="button"
                      disabled={disconnect.isPending}
                      aria-busy={disconnect.isPending}
                      onClick={() => handleDisconnect(active)}
                    >
                      {copy.settings.integrations.gmailDisconnectDialogConfirm}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          );
        })()}
      </CardContent>
      {toast !== null && (
        <ToastProvider duration={5000}>
          <Toast open onOpenChange={(o) => !o && setToast(null)}>
            <div className="flex flex-col gap-1">
              <ToastTitle>{toast.title}</ToastTitle>
              {toast.body !== undefined && (
                <span className="text-caption text-text-secondary">{toast.body}</span>
              )}
            </div>
            <ToastClose />
          </Toast>
          <ToastViewport />
        </ToastProvider>
      )}
    </Card>
  );
}
